import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { 
  useConversations, 
  useConversation, 
  useCreateConversation,
  useSendMessage,
  useDeleteMessage,
  useMessageableUsers
} from '../lib/hooks';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Send, 
  Paperclip, 
  X, 
  Search,
  Plus,
  ArrowLeft,
  Loader2,
  Trash2,
  Download,
  FileText,
  Image as ImageIcon,
  Info,
  Users
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../lib/translations';

const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const formatDateSeparator = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) {
    return "Aujourd'hui";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Hier";
  } else {
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  }
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `${minutes}min`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const getRoleBadge = (role) => {
  const roles = {
    admin: { label: 'Admin', className: 'bg-primary/10 text-primary' },
    teacher: { label: 'Enseignant', className: 'bg-blue-100 text-blue-700' },
    student: { label: 'Étudiant', className: 'bg-green-100 text-green-700' },
    partner: { label: 'Partenaire', className: 'bg-purple-100 text-purple-700' },
    resp_peda: { label: 'Resp. Péda', className: 'bg-orange-100 text-orange-700' },
    resp_admin: { label: 'Resp. Admin', className: 'bg-teal-100 text-teal-700' },
  };
  const r = roles[role] || { label: role, className: 'bg-gray-100 text-gray-700' };
  return <Badge className={r.className}>{r.label}</Badge>;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const isImageFile = (mimeType) => {
  return mimeType && mimeType.startsWith('image/');
};

// New Conversation Sheet Component
const NewConversationSheet = ({ open, onOpenChange, onSuccess }) => {
  const [search, setSearch] = useState('');
  const { lang } = useLanguage();
  const [selectedUser, setSelectedUser] = useState(null);
  const [subject, setSubject] = useState('');
  const [firstMessage, setFirstMessage] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);
  const newConvFileRef = useRef(null);
  
  const { data: users = [], isLoading } = useMessageableUsers(search);
  const createConversation = useCreateConversation();
  const [sendingMessage, setSendingMessage] = useState(false);
  
  const handleFileAttach = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10MB)');
        return;
      }
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Type de fichier non autorisé (PDF, JPG, PNG, DOCX)');
        return;
      }
      setAttachedFile(file);
    }
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!selectedUser) {
      toast.error('Veuillez sélectionner un destinataire');
      return;
    }
    if (!firstMessage.trim() && !attachedFile) {
      toast.error('Veuillez écrire un message ou joindre un fichier');
      return;
    }
    
    setSendingMessage(true);
    try {
      // Create conversation
      const result = await createConversation.mutateAsync({
        participant_ids: [selectedUser.id],
        subject: subject || null
      });
      
      // Send first message using the API directly
      const { messagingApi } = await import('../lib/api');
      await messagingApi.sendMessage(result.id, firstMessage.trim() || null, attachedFile);
      
      toast.success(result.existing ? 'Conversation existante ouverte' : 'Conversation créée');
      onSuccess?.(result.id);
      onOpenChange(false);
      
      // Reset form
      setSelectedUser(null);
      setSubject('');
      setFirstMessage('');
      setAttachedFile(null);
      setSearch('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSendingMessage(false);
    }
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t('messages_new_conv', lang)}</SheetTitle>
          <SheetDescription>
            Démarrez une nouvelle conversation avec un membre de l'équipe
          </SheetDescription>
        </SheetHeader>
        
        <div className="space-y-6 mt-6">
          <div className="space-y-2">
            <Label>Destinataire *</Label>
            {selectedUser ? (
              <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-primary text-white text-sm">
                      {selectedUser.first_name[0]}{selectedUser.last_name[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedUser.first_name} {selectedUser.last_name}</p>
                    <p className="text-xs text-muted-foreground">{selectedUser.email}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedUser(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher un utilisateur..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="user-search-input"
                  />
                </div>
                <ScrollArea className="h-48 border rounded-lg">
                  {isLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : users.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Aucun utilisateur trouvé
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {users.map(user => (
                        <div
                          key={user.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                          onClick={() => setSelectedUser(user)}
                          data-testid={`user-option-${user.id}`}
                        >
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-secondary text-white text-xs">
                              {user.first_name[0]}{user.last_name[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {user.first_name} {user.last_name}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          </div>
                          {getRoleBadge(user.role)}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label>{lang === 'de' ? 'Betreff (optional)' : 'Sujet (optionnel)'}</Label>
            <Input
              placeholder="Ex: Question sur mon inscription"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Premier message *</Label>
            <Textarea
              placeholder="Écrivez votre message..."
              value={firstMessage}
              onChange={(e) => setFirstMessage(e.target.value)}
              rows={4}
              data-testid="first-message-input"
            />
          </div>

          {/* File attachment */}
          <div className="space-y-2">
            <input
              type="file"
              ref={newConvFileRef}
              onChange={handleFileAttach}
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.docx"
            />
            {attachedFile ? (
              <div className="flex items-center gap-2 p-2 border rounded-lg bg-muted/50">
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm truncate flex-1">{attachedFile.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {(attachedFile.size / 1024).toFixed(0)} KB
                </span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setAttachedFile(null)}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => newConvFileRef.current?.click()}
                className="w-full"
                data-testid="new-conv-attach-btn"
              >
                <Paperclip className="w-4 h-4 mr-2" />
                Joindre un fichier (PDF, JPG, PNG, DOCX)
              </Button>
            )}
          </div>
        </div>
        
        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!selectedUser || (!firstMessage.trim() && !attachedFile) || sendingMessage}
            className="bg-primary hover:bg-primary/90"
            data-testid="start-conversation-btn"
          >
            {sendingMessage && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Démarrer la conversation
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

// Conversation List Item Component
const ConversationItem = ({ conversation, isActive, onClick }) => {
  const participant = conversation.participants[0];
  
  return (
    <div
      className={`p-3 cursor-pointer transition-colors border-l-4 ${
        isActive 
          ? 'bg-primary/5 border-primary' 
          : conversation.unread_count > 0 
            ? 'bg-blue-50/50 border-secondary hover:bg-muted' 
            : 'border-transparent hover:bg-muted'
      }`}
      onClick={onClick}
      data-testid={`conversation-item-${conversation.id}`}
    >
      <div className="flex items-start gap-3">
        <Avatar className="w-10 h-10 shrink-0">
          <AvatarFallback className={isActive ? 'bg-primary text-white' : 'bg-secondary text-white'}>
            {participant?.avatar_initials || '??'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className={`text-sm truncate ${conversation.unread_count > 0 ? 'font-bold' : 'font-medium'}`}>
              {participant ? `${participant.first_name} ${participant.last_name}` : 'Inconnu'}
            </p>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatRelativeTime(conversation.last_message_at || conversation.updated_at)}
            </span>
          </div>
          {conversation.subject && (
            <p className="text-xs text-muted-foreground truncate">{conversation.subject}</p>
          )}
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-muted-foreground truncate">
              {conversation.last_message_preview || 'Nouvelle conversation'}
            </p>
            {conversation.unread_count > 0 && (
              <Badge className="bg-secondary text-white text-xs ml-2 shrink-0">
                {conversation.unread_count}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Message Bubble Component
const MessageBubble = ({ message, isOwn, onDelete, canDelete }) => {
  const [showDelete, setShowDelete] = useState(false);
  
  const isDeleted = !!message.deleted_at;
  
  return (
    <div 
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
    >
      <div className={`flex items-end gap-2 max-w-[75%] ${isOwn ? 'flex-row-reverse' : ''}`}>
        {!isOwn && (
          <Avatar className="w-8 h-8 shrink-0">
            <AvatarFallback className="bg-muted text-muted-foreground text-xs">
              {message.sender_initials}
            </AvatarFallback>
          </Avatar>
        )}
        <div className={`rounded-2xl px-4 py-2 ${
          isOwn 
            ? 'bg-primary text-white rounded-br-md' 
            : 'bg-muted text-foreground rounded-bl-md'
        }`}>
          {!isOwn && !isDeleted && (
            <p className={`text-xs font-medium mb-1 ${isOwn ? 'text-white/80' : 'text-muted-foreground'}`}>
              {message.sender_name}
            </p>
          )}
          
          {isDeleted ? (
            <p className="text-sm italic opacity-60">{t('messages_deleted', lang)}</p>
          ) : (
            <>
              {message.content && (
                <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
              )}
              {message.file_name && (
                <div className={`mt-2 p-2 rounded-lg ${isOwn ? 'bg-white/10' : 'bg-background'}`}>
                  {isImageFile(message.file_type) ? (
                    <a 
                      href={`${process.env.REACT_APP_BACKEND_URL}${message.file_url}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${message.file_url}`}
                        alt={message.file_name}
                        className="max-w-full max-h-48 rounded object-cover"
                      />
                    </a>
                  ) : (
                    <a 
                      href={`${process.env.REACT_APP_BACKEND_URL}${message.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 text-sm ${isOwn ? 'text-white' : 'text-foreground'}`}
                    >
                      <FileText className="w-4 h-4" />
                      <span className="truncate flex-1">{message.file_name}</span>
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                  <p className={`text-xs mt-1 ${isOwn ? 'text-white/60' : 'text-muted-foreground'}`}>
                    {formatFileSize(message.file_size)}
                  </p>
                </div>
              )}
            </>
          )}
          
          <p className={`text-xs mt-1 ${isOwn ? 'text-white/60' : 'text-muted-foreground'}`}>
            {formatTime(message.created_at)}
          </p>
        </div>
        
        {canDelete && showDelete && !isDeleted && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(message.id)}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Main Messages Page Component
const MessagesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [searchParams] = useSearchParams();
  const conversationIdFromUrl = searchParams.get('id');
  
  const [selectedConversationId, setSelectedConversationId] = useState(conversationIdFromUrl);
  const [searchQuery, setSearchQuery] = useState('');
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isMobileListView, setIsMobileListView] = useState(true);
  const [infoPopoverOpen, setInfoPopoverOpen] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  
  const { data: conversations = [], isLoading: convLoading } = useConversations(searchQuery);
  const { data: conversation, isLoading: msgLoading } = useConversation(selectedConversationId);
  const sendMessage = useSendMessage(selectedConversationId);
  const deleteMessage = useDeleteMessage(selectedConversationId);
  
  // Update URL when conversation changes
  useEffect(() => {
    if (selectedConversationId) {
      navigate(`/messages?id=${selectedConversationId}`, { replace: true });
    }
  }, [selectedConversationId, navigate]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation?.messages]);
  
  // Handle mobile view
  useEffect(() => {
    if (selectedConversationId) {
      setIsMobileListView(false);
    }
  }, [selectedConversationId]);
  
  const handleSelectConversation = (convId) => {
    setSelectedConversationId(convId);
    setIsMobileListView(false);
  };
  
  const handleBackToList = () => {
    setIsMobileListView(true);
    setSelectedConversationId(null);
    navigate('/messages', { replace: true });
  };
  
  const handleSendMessage = async () => {
    if ((!messageText.trim() && !selectedFile) || sendMessage.isPending) return;
    
    try {
      await sendMessage.mutateAsync({ content: messageText.trim() || null, file: selectedFile });
      setMessageText('');
      setSelectedFile(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };
  
  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10MB)');
        return;
      }
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Type de fichier non autorisé');
        return;
      }
      setSelectedFile(file);
    }
    e.target.value = '';
  };
  
  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteMessage.mutateAsync(messageId);
      toast.success('Message supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };
  
  const handleNewConversationSuccess = (newConvId) => {
    setSelectedConversationId(newConvId);
    setIsMobileListView(false);
  };
  
  // Auto-resize textarea
  const handleTextareaChange = (e) => {
    setMessageText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };
  
  // Group messages by date
  const groupMessagesByDate = useCallback((messages) => {
    if (!messages) return [];
    const groups = [];
    let currentDate = null;
    
    messages.forEach(msg => {
      const msgDate = new Date(msg.created_at).toDateString();
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ type: 'date', date: msg.created_at });
      }
      groups.push({ type: 'message', message: msg });
    });
    
    return groups;
  }, []);
  
  const messageGroups = groupMessagesByDate(conversation?.messages);
  
  return (
    <div className="h-[calc(100vh-140px)] min-h-[500px] animate-fade-in" data-testid="messages-page">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 h-full border rounded-xl overflow-hidden bg-white shadow-sm">
        {/* Left Panel - Conversation List */}
        <div className={`md:col-span-1 border-r flex flex-col ${
          !isMobileListView ? 'hidden md:flex' : 'flex'
        }`}>
          {/* Header */}
          <div className="p-4 border-b space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold">{t('messages_title', lang)}</h2>
              <Button 
                size="sm" 
                onClick={() => setNewConversationOpen(true)}
                className="bg-primary hover:bg-primary/90"
                data-testid="new-conversation-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nouveau
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t("action_search", lang)}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="conversation-search"
              />
            </div>
          </div>
          
          {/* Conversation List */}
          <ScrollArea className="flex-1">
            {convLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 px-4">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">{t('messages_no_conv', lang)}</p>
                <Button 
                  variant="link" 
                  className="mt-2 text-primary"
                  onClick={() => setNewConversationOpen(true)}
                >
                  Démarrer une conversation
                </Button>
              </div>
            ) : (
              <div className="divide-y">
                {conversations.map(conv => (
                  <ConversationItem
                    key={conv.id}
                    conversation={conv}
                    isActive={conv.id === selectedConversationId}
                    onClick={() => handleSelectConversation(conv.id)}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
        
        {/* Right Panel - Active Conversation */}
        <div className={`md:col-span-2 flex flex-col ${
          isMobileListView ? 'hidden md:flex' : 'flex'
        }`}>
          {!selectedConversationId ? (
            /* Empty State */
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                <h3 className="font-heading text-xl font-semibold mb-2">
                  Sélectionnez une conversation
                </h3>
                <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                  Choisissez une conversation dans la liste ou créez-en une nouvelle pour commencer à discuter.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Conversation Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    onClick={handleBackToList}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  
                  {conversation?.participants?.length === 1 ? (
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-primary text-white">
                        {conversation.participants[0]?.avatar_initials}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <Users className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  
                  <div>
                    <p className="font-medium">
                      {conversation?.participants?.map(p => `${p.first_name} ${p.last_name}`).join(', ') || 'Chargement...'}
                    </p>
                    {conversation?.subject && (
                      <p className="text-xs text-muted-foreground">{conversation.subject}</p>
                    )}
                  </div>
                </div>
                
                <Popover open={infoPopoverOpen} onOpenChange={setInfoPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <Info className="w-5 h-5 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72">
                    <div className="space-y-4">
                      <h4 className="font-medium">Participants</h4>
                      {conversation?.participants?.map(p => (
                        <div key={p.id} className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-secondary text-white text-xs">
                              {p.avatar_initials}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {p.first_name} {p.last_name}
                            </p>
                            {getRoleBadge(p.role)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Messages Area */}
              <ScrollArea className="flex-1 p-4">
                {msgLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="space-y-1">
                    {messageGroups.map((item, idx) => {
                      if (item.type === 'date') {
                        return (
                          <div key={`date-${idx}`} className="flex justify-center my-4">
                            <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                              {formatDateSeparator(item.date)}
                            </span>
                          </div>
                        );
                      }
                      const msg = item.message;
                      const isOwn = msg.sender_id === user?.id;
                      const canDelete = isOwn || user?.role === 'admin';
                      return (
                        <MessageBubble
                          key={msg.id}
                          message={msg}
                          isOwn={isOwn}
                          onDelete={handleDeleteMessage}
                          canDelete={canDelete}
                        />
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
              
              {/* Input Area */}
              <div className="p-4 border-t bg-muted/30">
                {/* Selected File Preview */}
                {selectedFile && (
                  <div className="mb-3 flex items-center gap-2 p-2 bg-background rounded-lg border">
                    {selectedFile.type.startsWith('image/') ? (
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <FileText className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className="text-sm truncate flex-1">{selectedFile.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatFileSize(selectedFile.size)}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => setSelectedFile(null)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                
                <div className="flex gap-2 items-end">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.docx"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sendMessage.isPending}
                    data-testid="attach-file-btn"
                  >
                    <Paperclip className="w-5 h-5" />
                  </Button>
                  
                  <Textarea
                    ref={textareaRef}
                    placeholder="Écrivez votre message..."
                    value={messageText}
                    onChange={handleTextareaChange}
                    onKeyDown={handleKeyDown}
                    className="flex-1 min-h-[40px] max-h-[120px] resize-none"
                    rows={1}
                    data-testid="message-input"
                  />
                  
                  <Button
                    onClick={handleSendMessage}
                    disabled={(!messageText.trim() && !selectedFile) || sendMessage.isPending}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="send-message-btn"
                  >
                    {sendMessage.isPending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
                
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Cmd/Ctrl + Entrée pour envoyer
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* New Conversation Sheet */}
      <NewConversationSheet
        open={newConversationOpen}
        onOpenChange={setNewConversationOpen}
        onSuccess={handleNewConversationSuccess}
      />
    </div>
  );
};

export default MessagesPage;
